/**
 * Date and time formatting utilities
 */

// ============================================================================
// Constants
// ============================================================================

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

// ============================================================================
// Internal Helpers
// ============================================================================

/**
 * Parse and validate a date input
 * @returns Valid Date object or null if invalid
 */
const parseDate = (dateStr: string | Date | undefined | null): Date | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

// ============================================================================
// Grouping Utilities
// ============================================================================

/**
 * Interface for items that can be grouped by time
 */
interface TimeGroupableItem {
  updated_at: string;
}

/**
 * Groups items by time period (today, yesterday, this week, older)
 */
export const groupByTime = <T extends TimeGroupableItem>(items: T[]): {
  today: T[];
  yesterday: T[];
  thisWeek: T[];
  older: T[];
} => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - MS_PER_DAY);
  const sevenDaysAgo = new Date(now.getTime() - 7 * MS_PER_DAY);

  const groups = {
    today: [] as T[],
    yesterday: [] as T[],
    thisWeek: [] as T[],
    older: [] as T[],
  };

  items.forEach(item => {
    const date = new Date(item.updated_at);
    if (date >= todayStart) groups.today.push(item);
    else if (date >= yesterdayStart) groups.yesterday.push(item);
    else if (date >= sevenDaysAgo) groups.thisWeek.push(item);
    else groups.older.push(item);
  });

  return groups;
};

// ============================================================================
// Relative Time Formatting
// ============================================================================

/**
 * Format a date as relative time (e.g., "5h ago", "3d ago", "in 2h")
 * Handles both past and future dates.
 * @param dateStr - Date string or Date object
 * @returns Formatted relative time string, or 'Never' for missing/invalid dates
 */
export const formatRelativeTime = (dateStr: string | Date | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return 'Never';

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  // Handle future dates
  if (diff < 0) {
    const absDiff = Math.abs(diff);
    const absMinutes = Math.floor(absDiff / MS_PER_MINUTE);
    const absHours = Math.floor(absDiff / MS_PER_HOUR);
    const absDays = Math.floor(absDiff / MS_PER_DAY);
    if (absDays > 0) return `in ${absDays}d`;
    if (absHours > 0) return `in ${absHours}h`;
    if (absMinutes > 0) return `in ${absMinutes}m`;
    return 'now';
  }

  // Handle past dates
  const minutes = Math.floor(diff / MS_PER_MINUTE);
  const hours = Math.floor(diff / MS_PER_HOUR);
  const days = Math.floor(diff / MS_PER_DAY);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
};

/**
 * Format a date as friendly relative time with Today/Yesterday labels.
 * Good for displaying "Updated X ago" style timestamps.
 * @param dateStr - Date string or Date object
 * @returns Formatted string like "Today", "Yesterday", "3d ago", "2w ago", "1mo ago", "1y ago"
 */
export const formatTimeAgo = (dateStr: string | Date | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return 'Never';

  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / MS_PER_DAY);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

/**
 * Format a timestamp for activity feeds - shows relative time for recent, date for older
 * Returns "Just now", "Xm ago", "Xh ago" for today, then switches to date format
 * @param dateStr - Date string or Date object
 * @returns Formatted time string, or empty string for invalid/missing dates
 */
export const formatActivityTime = (dateStr: string | Date | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return '';

  const now = new Date();
  const diffSeconds = (now.getTime() - date.getTime()) / MS_PER_SECOND;

  // Handle future dates - show date format instead of incorrect "Just now"
  if (diffSeconds < 0) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  if (diffSeconds < 60) return 'Just now';
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ============================================================================
// Absolute Date/Time Formatting
// ============================================================================

/**
 * Format a date and time for display (e.g., "Dec 15, 2024 at 3:45 PM")
 * @param dateStr - Date string or Date object
 * @returns Formatted date and time string
 */
export const formatDateTime = (dateStr: string | Date | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return '-';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format a date with weekday (e.g., "Fri, Jan 15, 10:30 AM")
 * @param dateStr - Date string or Date object
 * @returns Formatted date string with weekday, or '-' for invalid/missing dates
 */
export const formatDateTimeWithWeekday = (dateStr: string | Date | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return '-';

  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format a timestamp for chat messages.
 * Shows just time for today (e.g., "10:30 AM"), includes date for older messages (e.g., "Dec 15, 10:30 AM")
 * @param dateStr - Date object or string
 * @returns Formatted timestamp string
 */
export const formatChatTimestamp = (dateStr: Date | string | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return '';

  const now = new Date();
  const isToday = now.toDateString() === date.toDateString();

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (isToday) {
    return timeStr;
  }

  const dateOnlyStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return `${dateOnlyStr}, ${timeStr}`;
};

// ============================================================================
// Date-Only Formatting
// ============================================================================

/**
 * Format a date as short month + day, no year (e.g., "Aug 8")
 * @param dateStr - Date string or Date object
 * @returns Formatted date string, or '-' for invalid/missing dates
 */
export const formatShortDate = (dateStr: string | Date | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return '-';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format a date only with short month (e.g., "Jan 15, 2024")
 * @param dateStr - Date string or Date object
 * @returns Formatted date string
 */
export const formatDateOnly = (dateStr: string | Date | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return '-';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a date with long month (e.g., "January 15, 2024")
 * @param dateStr - Date string or Date object
 * @returns Formatted date string, or '-' for invalid/missing dates
 */
export const formatDateLong = (dateStr: string | Date | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return '-';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format a date for use in filenames (e.g., "2024-01-15")
 * @param date - Date object (defaults to current date)
 * @returns ISO date string in YYYY-MM-DD format
 */
export const formatDateForFilename = (date: Date = new Date()): string => {
  return date.toISOString().split('T')[0];
};

// ============================================================================
// Time-Only Formatting
// ============================================================================

/**
 * Format time with seconds (e.g., "10:30:45 AM")
 * @param dateStr - Date string or Date object
 * @returns Formatted time string with seconds, or '-' for invalid/missing dates
 */
export const formatTimeWithSeconds = (dateStr: string | Date | undefined): string => {
  const date = parseDate(dateStr);
  if (!date) return '-';

  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// ============================================================================
// Input Format Utilities
// ============================================================================

/**
 * Convert a date to local datetime-local input format (YYYY-MM-DDTHH:mm)
 * Used for populating <input type="datetime-local"> fields with proper timezone handling.
 * @param dateStr - Date string (typically UTC/ISO format) or Date object
 * @returns Local datetime string in YYYY-MM-DDTHH:mm format, or empty string for invalid/missing dates
 */
export const toLocalDatetimeInput = (dateStr: string | Date | undefined | null): string => {
  const date = parseDate(dateStr);
  if (!date) return '';

  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** A calendar day (YYYY-MM-DD) as "Mar 3, 2027", the same day wherever the viewer is. */
export const formatCalendarDay = (day: string): string => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** "09:00:00" -> "9:00": a wall-clock time without seconds or a leading zero. */
export const formatClockTime = (time: string): string => time.replace(/^0?(\d{1,2}:\d{2}).*$/, '$1');

/** A sync stamp as "Oct 7, 6:40 PM", in the viewer's zone, for "as of" lines. */
export const formatAsOf = (iso: string): string =>
  new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const zonedParts = (date: Date, timeZone: string): Record<string, number> =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    }).formatToParts(date).filter((p) => p.type !== 'literal').map((p) => [p.type, Number(p.value)]),
  );

/** An instant as a datetime-local value ("2026-10-22T19:00") on the wall clock of timeZone, or '' if unparseable. */
export const toZonedDatetimeInput = (iso: string, timeZone: string): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const p = zonedParts(at, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
};

/** A datetime-local value read on the wall clock of timeZone, as a UTC ISO instant; null if unparseable. */
export const fromZonedDatetimeInput = (wall: string, timeZone: string): string | null => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(wall)) return null;
  const [y, mo, d, h, mi] = wall.split(/[-T:]/).map(Number);
  const naive = Date.UTC(y, mo - 1, d, h, mi);
  const offset = (utc: number) => {
    const p = zonedParts(new Date(utc), timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(utc / 60000) * 60000;
  };
  return new Date(naive - offset(naive - offset(naive))).toISOString();
};
